import type { 
  Chat, 
  Message, 
  User, 
  ApiResponse, 
  PaginatedResponse 
} from '@/types';

interface ApiConfig {
  baseUrl: string;
  timeout?: number;
  retries?: number;
}

class ApiService {
  private baseUrl: string;
  private timeout: number;
  private retries: number;
  private authTokenProvider: (() => Promise<string | null>) | null = null;

  constructor(config: ApiConfig) {
    this.baseUrl = config.baseUrl.replace(/\/$/, ''); // Remove trailing slash
    this.timeout = config.timeout || 10000;
    this.retries = config.retries || 3;
  }

  /**
   * Set authentication token provider
   */
  setAuthTokenProvider(provider: () => Promise<string | null>): void {
    this.authTokenProvider = provider;
  }

  /**
   * Get headers for API requests
   */
  private async getHeaders(): Promise<HeadersInit> {
    const headers: HeadersInit = {
      'Content-Type': 'application/json',
    };

    if (this.authTokenProvider) {
      const token = await this.authTokenProvider();
      if (token) {
        headers.Authorization = `Bearer ${token}`;
      }
    }

    return headers;
  }

  /**
   * Make HTTP request with retry logic
   */
  private async request<T>(
    endpoint: string, 
    options: RequestInit = {}, 
    retryCount = 0
  ): Promise<ApiResponse<T>> {
    try {
      const url = `${this.baseUrl}${endpoint}`;
      const headers = await this.getHeaders();
      
      const controller = new AbortController();
      const timeoutId = setTimeout(() => controller.abort(), this.timeout);

      const response = await fetch(url, {
        ...options,
        headers: {
          ...headers,
          ...options.headers,
        },
        signal: controller.signal,
      });

      clearTimeout(timeoutId);

      if (!response.ok) {
        const errorData = await response.json().catch(() => ({}));
        throw new Error(errorData.message || `HTTP ${response.status}: ${response.statusText}`);
      }

      const data = await response.json();
      return { data };
    } catch (error) {
      // Retry logic for network errors
      if (retryCount < this.retries && this.isRetryableError(error)) {
        await this.delay(Math.pow(2, retryCount) * 1000); // Exponential backoff
        return this.request<T>(endpoint, options, retryCount + 1);
      }

      return {
        error: error instanceof Error ? error.message : 'Network request failed'
      };
    }
  }

  private isRetryableError(error: unknown): boolean {
    if (error instanceof Error) {
      return error.name === 'AbortError' || 
             error.message.includes('fetch') ||
             error.message.includes('network');
    }
    return false;
  }

  private delay(ms: number): Promise<void> {
    return new Promise(resolve => setTimeout(resolve, ms));
  }

  // Chat Operations
  async getChats(): Promise<ApiResponse<Chat[]>> {
    return this.request<Chat[]>('/api/chats');
  }

  async getChat(chatId: string): Promise<ApiResponse<Chat>> {
    return this.request<Chat>(`/api/chats/${chatId}`);
  }

  async createChat(participants: string[], name?: string): Promise<ApiResponse<Chat>> {
    return this.request<Chat>('/api/chats', {
      method: 'POST',
      body: JSON.stringify({ participants, name }),
    });
  }

  async updateChat(chatId: string, updates: Partial<Chat>): Promise<ApiResponse<Chat>> {
    return this.request<Chat>(`/api/chats/${chatId}`, {
      method: 'PATCH',
      body: JSON.stringify(updates),
    });
  }

  async deleteChat(chatId: string): Promise<ApiResponse<void>> {
    return this.request<void>(`/api/chats/${chatId}`, {
      method: 'DELETE',
    });
  }

  // Message Operations
  async getMessages(
    chatId: string, 
    page = 1, 
    limit = 50
  ): Promise<ApiResponse<PaginatedResponse<Message>>> {
    const params = new URLSearchParams({
      page: page.toString(),
      limit: limit.toString(),
    });
    
    return this.request<PaginatedResponse<Message>>(
      `/api/chats/${chatId}/messages?${params}`
    );
  }

  async sendMessage(
    chatId: string, 
    content: string, 
    type: string = 'text'
  ): Promise<ApiResponse<Message>> {
    return this.request<Message>(`/api/chats/${chatId}/messages`, {
      method: 'POST',
      body: JSON.stringify({ content, type }),
    });
  }

  async updateMessage(
    chatId: string, 
    messageId: string, 
    content: string
  ): Promise<ApiResponse<Message>> {
    return this.request<Message>(`/api/chats/${chatId}/messages/${messageId}`, {
      method: 'PATCH',
      body: JSON.stringify({ content }),
    });
  }

  async deleteMessage(chatId: string, messageId: string): Promise<ApiResponse<void>> {
    return this.request<void>(`/api/chats/${chatId}/messages/${messageId}`, {
      method: 'DELETE',
    });
  }

  async markMessageAsRead(chatId: string, messageId: string): Promise<ApiResponse<void>> {
    return this.request<void>(`/api/chats/${chatId}/messages/${messageId}/read`, {
      method: 'POST',
    });
  }

  // User Operations
  async getUsers(): Promise<ApiResponse<User[]>> {
    return this.request<User[]>('/api/users');
  }

  async getUser(userId: string): Promise<ApiResponse<User>> {
    return this.request<User>(`/api/users/${userId}`);
  }

  async updateProfile(updates: Partial<User>): Promise<ApiResponse<User>> {
    return this.request<User>('/api/users/profile', {
      method: 'PATCH',
      body: JSON.stringify(updates),
    });
  }

  async searchUsers(query: string): Promise<ApiResponse<User[]>> {
    const params = new URLSearchParams({ q: query });
    return this.request<User[]>(`/api/users/search?${params}`);
  }

  // Status Operations
  async updateUserStatus(status: string): Promise<ApiResponse<void>> {
    return this.request<void>('/api/users/status', {
      method: 'POST',
      body: JSON.stringify({ status }),
    });
  }

  async getOnlineUsers(): Promise<ApiResponse<User[]>> {
    return this.request<User[]>('/api/users/online');
  }

  // Health Check
  async healthCheck(): Promise<ApiResponse<{ status: string; timestamp: string }>> {
    return this.request<{ status: string; timestamp: string }>('/api/health');
  }
}

export default ApiService;